import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Link } from 'react-router-dom';

import { ExemplaresDaLauda } from '../components/ExemplaresDaLauda';
import { FaixaDaEsteira } from '../components/FaixaDaEsteira';
import { PecasDoFato } from '../components/PecasDoFato';
import {
  jornaisApi,
  type ContratoAviso,
  type FatoDaLauda,
  type Lauda,
} from '@/services/jornais';

import styles from './Jornais.module.css';

/** O chip do fato. Componente proprio para o tipo chegar estreitado — sem
 *  assercao nao-nula, que o lint da casa proibe. */
function FatoDoCard({
  fato,
  aoAbrir,
}: {
  fato: FatoDaLauda;
  aoAbrir: (id: string) => void;
}) {
  return (
    <button
      type="button"
      className={`${styles.chip} ${styles.chipFato}`}
      title={`${fato.titulo} — ver as peças desta história`}
      onClick={() => aoAbrir(fato.id)}
    >
      fato · {fato.pecas} peças
    </button>
  );
}

/**
 * Jornais — as laudas do telejornal, por praça, jornal e dia.
 *
 * Até aqui a lauda que entrava pelo ION HUB só existia diluída no quadro de
 * Produção, misturada com trends, loteria e horóscopo. A redação de TV pensa em
 * outra ordem: *qual praça*, *qual jornal*, *que dia*. É essa ordem que a tela
 * usa — aba por praça, filtro de jornal e de dia.
 *
 * O botão gerar é o mesmo caminho da esteira automática, declarado como MANUAL:
 * por isso o contrato do fato tem teto `avisar` (ADR-0022) e a resposta pode
 * trazer um aviso. Ele nunca bloqueia por aqui, de propósito.
 */

/**
 * O estado em que o objeto da matéria se resolveu. "à espera" não é erro: a
 * mídia chega antes, junto ou depois, e travar o texto pela mídia pararia os
 * 100% dos stories pelos 40% que têm VT.
 */
const COMPLETUDE_ROTULO: Record<string, string> = {
  // `sem_midia` vira travessão de propósito: a coluna de mídia ao lado já disse
  // "só texto", e repetir a mesma informação em duas colunas faz o operador
  // procurar a diferença entre elas.
  sem_midia: '—',
  a_espera_de_midia: 'à espera do VT',
  completo: 'completo',
};

const ESTADO_CLASSE: Record<string, string> = {
  publicada: styles.ok,
  sugerida: styles.espera,
  criando: styles.espera,
  revisao: styles.espera,
  falhou: styles.ruim,
  'sem pauta': styles.neutro,
};

function Estado({ valor }: { valor: string }) {
  return <span className={`${styles.chip} ${ESTADO_CLASSE[valor] ?? styles.neutro}`}>{valor}</span>;
}

function Hora({ iso }: { iso: string | null }) {
  if (!iso) return <span className={styles.fraco}>—</span>;
  const d = new Date(iso);
  return <>{d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</>;
}

function AvisoContrato({ contrato }: { contrato: ContratoAviso }) {
  if (!contrato?.aviso) return null;
  return (
    <div className={styles.avisoContrato} role="status">
      <strong>Contrato do fato:</strong> {contrato.aviso}
    </div>
  );
}

type CategoriaFora = NonNullable<Lauda['oculta_por']>;

/** O que o gate deixa fora por padrão — os parâmetros ficam em /engenharia. */
const CATEGORIAS: { chave: CategoriaFora; rotulo: string }[] = [
  { chave: 'formato', rotulo: 'Formato no ARION (anúncio, estúdio, tarja)' },
  { chave: 'anatomia', rotulo: 'Anatomia do jornal' },
  { chave: 'servico', rotulo: 'Serviço (página fixa)' },
  { chave: 'sem_minimo', rotulo: 'Sem o mínimo' },
];

export function Jornais() {
  const [praca, setPraca] = useState<string>('');
  const [jornal, setJornal] = useState<string>('');
  const [dia, setDia] = useState<string>('');
  const [ultimo, setUltimo] = useState<{ id: number; contrato: ContratoAviso } | null>(null);
  // A GRADE e a entrada de propósito: quem abre esta tela quer saber o que o
  // jornal deu, e isso se lê em manchete, não em coluna. A tabela densa fica a
  // um clique porque é dela que o operador vive quando algo trava.
  const [visao, setVisao] = useState<'grade' | 'tabela'>('grade');
  // O fato aberto: a historia inteira, quando o operador clica no chip.
  const [fatoAberto, setFatoAberto] = useState<string | null>(null);
  const [aberta, setAberta] = useState<Lauda | null>(null);
  // Só o que passou no gate (ADM, 11/09/2026): o resto fica fora — contado, e a
  // um tique na caixa do filtro, para ninguém achar que a lauda não chegou.
  const [mostrar, setMostrar] = useState<CategoriaFora[]>([]);

  const qc = useQueryClient();
  const filtro = {
    praca_id: praca || undefined,
    jornal: jornal || undefined,
    dia: dia || undefined,
    mostrar: mostrar.length > 0 ? mostrar : undefined,
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ['jornais', 'laudas', praca, jornal, dia, mostrar.join(',')],
    queryFn: () => jornaisApi.listar(filtro),
  });

  const gerar = useMutation({
    mutationFn: (id: number) => jornaisApi.gerar(id),
    onSuccess: (res) => {
      setUltimo({ id: res.lauda_id, contrato: res.contrato ?? {} });
      void qc.invalidateQueries({ queryKey: ['jornais', 'laudas'] });
    },
  });

  const laudas: Lauda[] = data?.laudas ?? [];
  const catalogo = data?.catalogo ?? [];
  const ocultas = data?.ocultas ?? {};
  const totalOcultas = CATEGORIAS.reduce((soma, c) => soma + (ocultas[c.chave] ?? 0), 0);
  const alternar = (chave: CategoriaFora) =>
    setMostrar((atual) =>
      atual.includes(chave) ? atual.filter((c) => c !== chave) : [...atual, chave],
    );

  return (
    <div className={styles.pagina}>
      <header className={styles.cabecalho}>
        <h1>Jornais</h1>
        <p className={styles.subtitulo}>
          O que entrou pelo ION HUB, na ordem em que a redação de TV procura.
        </p>
      </header>

      {/* Abas por praça. "Todas" é a primeira porque a praça única é o caso
          comum e obrigar a escolher seria uma trava sem motivo. */}
      <nav className={styles.abas} aria-label="Praças">
        <button
          type="button"
          className={praca === '' ? styles.abaAtiva : styles.aba}
          onClick={() => setPraca('')}
        >
          Todas as praças
        </button>
        {(data?.pracas ?? []).map((p) => (
          <button
            key={p.id}
            type="button"
            className={praca === p.id ? styles.abaAtiva : styles.aba}
            onClick={() => setPraca(p.id)}
          >
            {p.nome}
          </button>
        ))}
      </nav>

      <div className={styles.filtros}>
        <label className={styles.campo}>
          <span>jornal</span>
          <select value={jornal} onChange={(e) => setJornal(e.target.value)}>
            <option value="">todos</option>
            {(data?.jornais ?? []).map((j) => (
              <option key={j.rotulo} value={j.rotulo}>
                {j.rotulo} ({j.total})
              </option>
            ))}
          </select>
        </label>

        <label className={styles.campo}>
          <span>dia</span>
          <select value={dia} onChange={(e) => setDia(e.target.value)}>
            <option value="">todos</option>
            {(data?.dias ?? []).map((d) => (
              <option key={d.dia} value={d.dia}>
                {new Date(`${d.dia}T12:00:00`).toLocaleDateString('pt-BR')} ({d.total})
              </option>
            ))}
          </select>
        </label>

        <span className={styles.contador}>
          {isLoading ? 'carregando…' : `${laudas.length} lauda(s)`}
        </span>
      </div>

      <details className={styles.filtroGate}>
        <summary>
          Filtro do gate · {totalOcultas} fora da lista
          {mostrar.length > 0 ? ` · mostrando ${mostrar.length} categoria(s)` : ''}
        </summary>
        <p className={styles.fraco}>
          Aparecem só as laudas que passaram no gate. Marque para ver também o que ficou fora.
          Os critérios ficam em Engenharia → Telejornal → Gate e régua.
        </p>
        <div className={styles.filtroGateOpcoes}>
          {CATEGORIAS.map((c) => (
            <label key={c.chave} className={styles.filtroGateOpcao}>
              <input
                type="checkbox"
                checked={mostrar.includes(c.chave)}
                onChange={() => alternar(c.chave)}
              />
              {c.rotulo} ({ocultas[c.chave] ?? 0})
            </label>
          ))}
        </div>
      </details>

      {ultimo && <AvisoContrato contrato={ultimo.contrato} />}
      {gerar.isError && (
        <div className={styles.avisoErro} role="alert">
          O motor recusou: {(gerar.error as Error)?.message ?? 'erro desconhecido'}
        </div>
      )}

      {isError && (
        <div className={styles.avisoErro} role="alert">
          Não deu para ler as laudas. Isso é falha de leitura, não ausência de lauda —
          a diferença importa antes de alguém concluir que o jornal não chegou.
        </div>
      )}

      <div className={styles.seletorVisao} role="group" aria-label="Como ver as laudas">
        <button
          type="button"
          className={visao === 'grade' ? styles.visaoAtiva : styles.visao}
          aria-pressed={visao === 'grade'}
          onClick={() => setVisao('grade')}
        >
          manchetes
        </button>
        <button
          type="button"
          className={visao === 'tabela' ? styles.visaoAtiva : styles.visao}
          aria-pressed={visao === 'tabela'}
          onClick={() => setVisao('tabela')}
        >
          tabela
        </button>
      </div>

      {visao === 'grade' && (
        <>
          <ul className={styles.grade}>
            {laudas.map((l) => (
              <li key={l.id}>
                <article
                  className={`${styles.card} ${ultimo?.id === l.id ? styles.linhaTocada : ''}`}
                >
                  <button
                    type="button"
                    className={styles.cardAbrir}
                    onClick={() => setAberta(l)}
                    aria-label={`Exemplares de: ${l.titulo || 'lauda sem título'}`}
                  >
                    <h2 className={styles.cardTitulo}>{l.titulo || <em>sem título</em>}</h2>
                  </button>

                  <p className={styles.cardOrigem}>
                    {l.jornal}
                    {l.praca_nome ? ` · ${l.praca_nome}` : ''} · <Hora iso={l.ar} />
                  </p>

                  {/* A faixa aqui é o que substitui as oito colunas da tabela:
                      de relance se vê onde parou, sem ler nenhum rótulo. */}
                  <FaixaDaEsteira catalogo={catalogo} resumo={l.esteira} compacta />

                  <div className={styles.cardRodape}>
                    <Estado valor={l.estado} />
                    {l.gate?.decisao === 'nota' && (
                      <span className={`${styles.chip} ${styles.neutro}`} title={l.gate.motivo}>
                        nota
                      </span>
                    )}
                    {l.gate?.decisao === 'fora' && (
                      <span className={`${styles.chip} ${styles.espera}`} title={l.gate.motivo}>
                        fora do gate
                      </span>
                    )}
                    {l.fato && l.fato.pecas > 1 && (
                      <FatoDoCard fato={l.fato} aoAbrir={setFatoAberto} />
                    )}
                    {l.itens_de_midia > 0 ? (
                      <span className={`${styles.chip} ${styles.ok}`}>{l.itens_de_midia} VT</span>
                    ) : (
                      <span className={styles.fraco}>só texto</span>
                    )}
                    <Link to={`/jornais/${l.id}`} className={styles.cardEsteira}>
                      esteira
                    </Link>
                  </div>

                  {l.gate?.decisao === 'fora' ? (
                    <p className={styles.fraco}>{l.gate.motivo}</p>
                  ) : (
                    l.erro && <p className={styles.erroLinha}>{l.erro}</p>
                  )}
                </article>
              </li>
            ))}
          </ul>
          {!isLoading && laudas.length === 0 && (
            <p className={styles.vazio}>
              Nenhuma lauda com esses filtros. Isso não quer dizer que o jornal não chegou —
              tire o filtro de dia para conferir.
            </p>
          )}
        </>
      )}

      {fatoAberto && (
        <PecasDoFato fatoId={fatoAberto} aoFechar={() => setFatoAberto(null)} />
      )}

      {aberta && (
        <ExemplaresDaLauda
          laudaId={aberta.id}
          titulo={aberta.titulo}
          aoFechar={() => setAberta(null)}
        />
      )}

      {visao === 'tabela' && (
      <table className={styles.tabela}>
        <thead>
          <tr>
            <th>#</th>
            <th>matéria</th>
            <th>jornal</th>
            <th>praça</th>
            <th>ar</th>
            <th>mídia</th>
            <th>lauda</th>
            <th>objeto</th>
            <th>esteira</th>
            <th>estado</th>
            <th aria-label="ações" />
          </tr>
        </thead>
        <tbody>
          {laudas.map((l) => (
            <tr key={l.id} className={ultimo?.id === l.id ? styles.linhaTocada : undefined}>
              <td className={styles.fraco}>{l.story_num ?? '—'}</td>
              <td>
                <Link to={`/jornais/${l.id}`} className={styles.titulo}>
                  {l.titulo || <em>sem título</em>}
                </Link>
                {l.erro && <div className={styles.erroLinha}>{l.erro}</div>}
              </td>
              <td>{l.jornal}</td>
              <td className={styles.fraco}>{l.praca_nome ?? '—'}</td>
              <td>
                <Hora iso={l.ar} />
                {!l.dia_e_do_ar && (
                  <span className={styles.fraco} title="o espelho não declarou hora do ar">
                    {' '}
                    (ingestão)
                  </span>
                )}
              </td>
              <td>
                {l.itens_de_midia > 0 ? (
                  <span className={`${styles.chip} ${styles.ok}`}>
                    {l.itens_de_midia} VT
                  </span>
                ) : (
                  <span className={styles.fraco}>só texto</span>
                )}
              </td>
              <td>
                <span className={`${styles.chip} ${l.stage === 'final' ? styles.ok : styles.espera}`}>
                  {l.stage}
                </span>
              </td>
              <td>
                <span
                  className={`${styles.chip} ${
                    l.completude === 'completo'
                      ? styles.ok
                      : l.completude === 'a_espera_de_midia'
                        ? styles.espera
                        : styles.neutro
                  }`}
                  title={
                    l.completude === 'a_espera_de_midia'
                      ? 'A materia pode ir ao ar assim; quando o VT chegar, ela e republicada com ele.'
                      : undefined
                  }
                >
                  {COMPLETUDE_ROTULO[l.completude] ?? l.completude}
                </span>
                <span className={styles.fraco}> v{l.versao}</span>
              </td>
              <td>
                {/* A faixa é RESUMO: o estado escrito está na tela da esteira.
                    Aqui ela serve para a redação achar o vermelho de relance. */}
                <FaixaDaEsteira catalogo={catalogo} resumo={l.esteira} compacta />
              </td>
              <td>
                <Estado valor={l.estado} />
              </td>
              <td>
                <button
                  type="button"
                  className={styles.botao}
                  disabled={gerar.isPending || !l.pauta_id}
                  title={
                    l.pauta_id
                      ? 'Manda o motor de escrita trabalhar nesta lauda agora'
                      : 'Esta lauda ainda não virou pauta'
                  }
                  onClick={() => gerar.mutate(l.id)}
                >
                  {gerar.isPending && gerar.variables === l.id ? 'gerando…' : 'gerar'}
                </button>
              </td>
            </tr>
          ))}
          {!isLoading && laudas.length === 0 && (
            <tr>
              <td colSpan={11} className={styles.vazio}>
                Nenhuma lauda com esses filtros. Isso não quer dizer que o jornal não
                chegou — tire o filtro de dia para conferir.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      )}
    </div>
  );
}
